import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

interface GridPagerProps {
  currentPage: number;
  totalResults: number;
  pageLength: number;
  baseUrl: string;
}

export const GridPager = ({
  currentPage,
  totalResults,
  pageLength,
  baseUrl,
}: GridPagerProps) => {
  const pageCount = useMemo<number>(() => {
    return Math.ceil(totalResults / pageLength);
  }, [totalResults, pageLength]);

  const pageUrls = useMemo(() => {
    const pages: ReactNode[] = new Array<number>(pageCount)
      .fill(0)
      .map((_page: number, index: number): ReactNode => {
        if (
          (index === currentPage - 3 && index > 1) ||
          (index === currentPage + 1 && index < pageCount - 2)
        ) {
          return (
            <li key={`pager-${index}`} className="ellipsis">
              ...
            </li>
          );
        } else {
          return (
            <li key={`pager-${index}`}>
              <Link
                className={cn('grid-pager-link', {
                  current: currentPage === index + 1,
                })}
                to={`${baseUrl}&page=${index + 1}`}
              >
                {index + 1}
              </Link>
            </li>
          );
        }
      })
      .filter((_pageLink: ReactNode, index: number): boolean => {
        return (
          pageCount < 10 ||
          index < 2 ||
          (index > currentPage - 4 && index < currentPage + 2) ||
          index > pageCount - 3
        );
      });

    return pages;
  }, [pageCount, currentPage, baseUrl]);

  if (pageUrls.length === 0) {
    return null;
  }


  return (
    <div className="pager-wrapper">
      <ul className="pager">
        {currentPage !== 1 && (
          <>
            <li className="jump prev">
              <Link to={`${baseUrl}&page=${currentPage - 1}`}>Previous</Link>
            </li>
          </>
        )}
        {pageUrls.map((pageUrl: ReactNode) => pageUrl)}
        {currentPage !== pageCount && (
          <>
            <li className="jump next">
              <Link to={`${baseUrl}&page=${currentPage + 1}`}>Next</Link>
            </li>
          </>
        )}
      </ul>
    </div>
  );
};
